import router from '@/router';
import { defineStore } from 'pinia';
import { useAccountStore } from '@/stores/account';
import { executeLlmTemplate, fetchLlmTemplate, createLlmTemplate, updateLlmTemplate } from '@/services/ppApi';

const PREFIX_MAPPING = {
    company_profile: 'company_profile',
    website_content: 'texts',
    searchresults: 'searchresults',
    pages: 'pages'
};

const DEFAULT_TEMPLATE = {
    template: "new",
    prompt: "{\"stop\": null, \"top_p\": 1, \"stream\": false, \"messages\": [{\"role\": \"system\", \"content\": \"Create a Business model analysis:\"}, {\"role\": \"user\", \"content\": \"Site:\\n[domain]\\n\\nCompany:\\n[texts.home_company]\\n\\nWebsite snippets:\\n[texts.all_metaarticle_best]\\n\\n[texts.search_snippets]\"}], \"max_tokens\": 4000, \"temperature\": 0.2, \"response_format\": {\"type\": \"json_object\"}}",
    model: "gemini-1.5-flash",
    provider: ""
};

const AVAILABLE_LLM_MODELS = [
    'deepseek-chat',
    'gpt-4o',
    'gpt-4o-mini',
    'gpt-4-turbo',
    'gpt-4o-2024-08-06',
    'gemini-1.5-pro',
    'gemini-1.5-flash',
    'gemini-1.5-flash-8b',
    'gemma2-9b-it',
    'Meta-Llama-3.1-405B-Instruct',
    'meta/llama3-405b-instruct-maas',
    'meta-llama/Meta-Llama-3.1-405B-Instruct',
    'llama3-8b-8192'
];

export const useLlmTemplateStore = defineStore('llmTemplate', {
    state: () => ({
        existingTemplate: false,
        loading: false,
        loadingVariables: false,
        loadingTest: false,
        loadingSave: false,
        loadingTemplates: false,
        hasSaved: false,
        template: null,
        templates: [],
        dataInputs: {
            id: {
                value: '',
                validation: 'company_profile'
            },
            domain: {
                value: '',
                validation: 'website_content'
            },
            manual_q: {
                value: '',
                validation: 'searchresults'
            },
            manual_url: {
                value: '',
                validation: 'pages.manual'
            }
        },
        variables: {
            company_profile: null,
            website_content: null,
            searchresults: null,
            pages: null
        },
        outputDefinition: '',
        response: {},
        responseError: null
    }),
    getters: {
        outputType(state) {
            return state.template?.prompt?.response_format?.type || 'text';
        },
        hasVariables(state) {
            return Object.values(state.variables).some(value => value);
        },
        hasDefaultTemplate(state) {
            return JSON.stringify(state.template) === JSON.stringify(this.parseTemplate(DEFAULT_TEMPLATE));
        },
        llmModelOptions() {
            return AVAILABLE_LLM_MODELS.map(model => ({ value: model, text: model, noTranslate: true }));
        },
        safePrompt(state) {
            const prompt = state.template.prompt;
            const safePrompt = { ...prompt };
            if (prompt.messages && Array.isArray(prompt.messages)) {
                safePrompt.messages = prompt.messages.map(msg => {
                    // eslint-disable-next-line no-unused-vars
                    const { uneditable, ...msgClone } = msg;
                    return msgClone;
                });
            }
            return safePrompt;
        },
        defaultTemplateOptions(state) {
            let templates = [...state.templates];
            // templates.unshift(DEFAULT_TEMPLATE);
            return templates;
        },
        validVariables(state) {
            const result = [];
        
            function extract(prefix, value) {
                if (Array.isArray(value)) {
                    result.push(prefix);
                    return;
                }

                if (value !== null && typeof value === 'object') {
                    Object.entries(value).forEach(([nestedKey, nestedVal]) => {
                        extract(`${prefix}.${nestedKey}`, nestedVal);
                    });
                }
                result.push(prefix);
            }
        
            Object.entries(state.variables).forEach(([varName, value]) => {
                const prefix = PREFIX_MAPPING[varName] || varName;
                if (value !== undefined && value !== null) {
                    extract(prefix, value);
                }
            });
            return result;
        },
        overviewVariables(state) {
            const overview = [];
        
            function extract(prefix, value) {
                overview.push({ variable: prefix, example: value });

                if (Array.isArray(value)) {
                    return;
                }

                if (value !== null && typeof value === 'object') {
                    Object.entries(value).forEach(([nestedKey, nestedVal]) => {
                        extract(`${prefix}.${nestedKey}`, nestedVal);
                    });
                }
            }
        
            Object.entries(state.variables).forEach(([varName, value]) => {
                const prefix = PREFIX_MAPPING[varName] || varName;
                if (value !== undefined && value !== null) {
                    extract(prefix, value);
                }
            });
            return overview;
        },
        usedVariables(state) {
            const regex = /\[([^\]]+)\]/g;
            const validFreq = {};
            const invalidSet = new Set();
            const messages = state.template?.prompt?.messages || [];
        
            messages.forEach(msg => {
                let match;
                while ((match = regex.exec(msg.content)) !== null) {
                    const variable = match[1];
                    if (state.validVariables.includes(variable)) {
                        validFreq[variable] = (validFreq[variable] || 0) + 1;
                    } else {
                        invalidSet.add(variable);
                    }
                }
            });
        
            const validVariables = Object.keys(validFreq);
            let duplicateValidVariables = [];
            Object.entries(validFreq).forEach(([varName, count]) => {
                if (count > 1) {
                    duplicateValidVariables.push({
                        name: varName,
                        count: count
                    });
                }
            });
        
            return {
                validVariables,
                invalidVariables: Array.from(invalidSet),
                duplicateValidVariables
            };
        },
        requiredDataInputs(state) {
            const used = state.usedVariables.validVariables || [];
            const activeInputs = {
                id: Boolean(state.dataInputs.id.value),
                domain: Boolean(state.dataInputs.domain.value),
                manual_q: Boolean(state.dataInputs.manual_q.value),
                manual_url: Boolean(state.dataInputs.manual_url.value)
            };
            const requiredInputs = {
                id: false,
                domain: false,
                manual_q: false,
                manual_url: false
            };
            const req = {
                company_profile: used.some(v => v.startsWith('company_profile')),
                texts: used.some(v => v.startsWith('texts')),
                searchresults: used.some(v => v.startsWith('searchresults') || v.startsWith('texts.search_')),
                pagesManual: used.some(v => v.startsWith('pages.manual') || v === 'pages'),
                pagesOther: used.some(v => v.startsWith('pages') && !v.startsWith('pages.manual'))
            };
    
            if (activeInputs.id && req.company_profile) {
                requiredInputs.id = true;
            }
    
            if (req.texts) {
                if (activeInputs.domain) {
                    requiredInputs.domain = true;
                } else if (activeInputs.id) {
                    requiredInputs.id = true;
                }
            }
    
            if (req.searchresults) {
                if (activeInputs.manual_q) {
                    requiredInputs.manual_q = true;
                } else if (activeInputs.domain) {
                    requiredInputs.domain = true;
                } else if (activeInputs.id) {
                    requiredInputs.id = true;
                }
            }
    
            if (req.pagesOther) {
                if (activeInputs.domain) {
                    requiredInputs.domain = true;
                } else if (activeInputs.id) {
                    requiredInputs.id = true;
                }
            }
    
            if (activeInputs.manual_url && req.pagesManual) {
                requiredInputs.manual_url = true;
            }
    
            return requiredInputs;
        },
        inputCounts(state) {
            let totalWords = 0;
            let totalCharacters = 0;
        
            // Helper function to convert a value into a string recursively.
            function flattenToString(value) {
                if (typeof value === 'string') return value;
                if (Array.isArray(value)) return value.map(flattenToString).join(' ');
                if (value !== null && typeof value === 'object') {
                    return Object.values(value).map(flattenToString).join(' ');
                }
                return String(value);
            }
        
            if (state.template && state.template.prompt && Array.isArray(state.template.prompt.messages)) {
                const overview = state.overviewVariables;
                state.template.prompt.messages.forEach(msg => {
                    const content = msg.content.replace(/\[([^\]]+)\]/g, (match, variable) => {
                        const found = overview.find(item => item.variable === variable);
                        return found ? flattenToString(found.example) : match;
                    });
                    totalCharacters += content.length;
                    totalWords += content.trim().split(/\s+/).filter(Boolean).length;
                });
            }
            return {
                words: totalWords,
                characters: totalCharacters
            };
        },
        exampleCall(state) {
            const tid = (state.template && state.template.id) || '[template_id]';
            const base = `https://api.bedrijfsdata.nl/v1.1/llm?template=${tid}`;
            
            const rawParams = [];
            const replacedParams = [];
            
            Object.keys(state.dataInputs).forEach(key => {
                const required = state.requiredDataInputs && state.requiredDataInputs[key];
                const value = state.dataInputs[key]?.value;
                if (required && value) {
                    rawParams.push(`${key}=[${key}]`);
                    replacedParams.push(`${key}=${value}`);
                }
            });

            const suffix = '&api_key=[your_api_key]&provider_key=[your_llm_api_key]';

            return {
                raw: base + (rawParams.length ? '&' + rawParams.join('&') : ''),
                replaced: base + (replacedParams.length ? '&' + replacedParams.join('&') : '') + suffix
            };
        }
    },
    actions: {
        async fetchTemplate(template) {
            const accountStore = useAccountStore();

            const payload = {
                account_id: accountStore.account.account_id,
                template
            };

            try {
                this.loading = true;
                const response = await fetchLlmTemplate(payload);

                if(response?.data?.llm_template?.length > 0) {
                    this.template = this.parseTemplate(response.data.llm_template[0]);
                    this.setTemplate(this.template);
                    this.existingTemplate = true;
                    return true;
                } else {
                    this.template = null;
                    return false;
                }
            } catch {
                return false;
            } finally {
                this.loading = false; 
            }
        },
        async fetchTemplates() {
            const accountStore = useAccountStore();

            const payload = {
                account_id: accountStore.account.account_id,
                type: 'example'
            };

            try {
                this.loadingTemplates = true;
                const response = await fetchLlmTemplate(payload);

                if(response?.data?.llm_template?.length > 0) {
                    this.templates = response.data.llm_template;
                    return true;
                } else {
                    this.templates = [];
                    return false;
                }
            } catch {
                return false;
            } finally {
                this.loadingTemplates = false; 
            }
        },
        resetTemplate() {
            this.$reset();
        },
        setDefaultTemplate() {
            const parsedTemplate = this.parseTemplate(DEFAULT_TEMPLATE);
            this.setTemplate(parsedTemplate);
        },
        setTemplate(template = null) {
            if (!this.template || this.template.template !== template.template) {
                this.template = template;
                this.fetchVariables();
                return;
            } else if (!template) {
                this.setDefaultTemplate();
                return;
            }
            this.template = { ...this.template, ...template };
            this.fetchVariables();
        },
        parseTemplate(template) {
            if (typeof template === 'string') {
                try {
                    template = JSON.parse(template);
                } catch {
                    return null;
                }
            }
            if (!template) return null;
            const result = {
                id: template.template ? template.template : this.template?.id ? this.template.id : '',
                display_name: template.display_name || '',
                description: template.description || '',
                model: template.model || 'gemini-1.5-flash',
                prompt: null
            };

            Object.keys(this.dataInputs).forEach(key => {
                if (template[key]) {
                    this.dataInputs[key].value = template[key];
                }
            });

            if (template.prompt) {
                try {
                    result.prompt = JSON.parse(decodeURIComponent(template.prompt));
                } catch {
                    result.prompt = template.prompt;
                }
            }

            const outputType = result.prompt?.response_format?.type || 'text';

            switch(outputType) {
                case 'json_object': {
                    const messages = result.prompt?.messages || [];
                    const outputDefMsg = messages.find(msg => msg.content.startsWith('----- JSON OUTPUT DEFINITION START -----'));
                    
                    if(outputDefMsg) {
                        outputDefMsg.uneditable = true;
                    } else {
                        messages.push({
                            role: 'user',
                            content: '----- JSON OUTPUT DEFINITION START -----\n\n----- JSON OUTPUT DEFINITION END -----',
                            uneditable: true
                        });
                    }

                    this.outputDefinition = outputDefMsg ? outputDefMsg.content.replace('----- JSON OUTPUT DEFINITION START -----', '').replace('----- JSON OUTPUT DEFINITION END -----', '').trim() : '';
                    break;
                }
                case 'json_schema':
                    this.outputDefinition = result.prompt?.response_format?.schema || '';
                    break;
                default:
                    this.outputDefinition = '';
            }

            return result;
        },
        clearVariables() {
            const variables = this.variables;
            Object.keys(variables).forEach(key => {
                this.variables[key] = '';
            });
        },
        async validateDataInputs(newDataInputs) {
            Object.keys(newDataInputs).forEach(key => {
                this.dataInputs[key].value = newDataInputs[key].value || '';
                this.dataInputs[key].status = newDataInputs[key].value ? 'unverified' : 'unused';
            });
            return await this.fetchVariables();
        },
        async fetchVariables() {
            const dataInputs = this.dataInputs;

            this.clearVariables();

            if (!Object.values(dataInputs).some(value => value.value)) {
                Object.keys(dataInputs).forEach(key => {
                    this.dataInputs[key].value = '';
                    this.dataInputs[key].status = 'unused';
                });
                return;
            }

            const accountStore = useAccountStore();
            const payload = {
                account_id: accountStore.account.account_id,
                template: 'bd_llm_template_variables',
                debug: 1
            };

            ['id', 'domain', 'manual_url', 'manual_q'].forEach(key => {
                if (dataInputs[key].value) payload[key] = dataInputs[key].value;
            });

            try {
                this.loadingVariables = true;
                const response = await executeLlmTemplate(payload);
                this.setVariables(response);
                return true;
            } catch {
                return false;
            } finally {
                this.loadingVariables = false;
            }
        },
        setVariables(response) {
            const dataInputs = this.dataInputs;

            this.variables.company_profile = response.data?.company_profile || null;
            this.variables.website_content = response.data?.texts || null;
            this.variables.searchresults = response.data?.searchresults || null;
            this.variables.pages = response.data?.pages || null;

            if (this.variables.pages && typeof this.variables.pages === 'object') {
                this.variables.pages = Object.fromEntries(
                    Object.entries(this.variables.pages)
                        .filter(([key, page]) => page.headlength && page.headlength > 0 || key === 'all')
                );
            }

            Object.keys(dataInputs).forEach(key => {
                const valid = dataInputs[key].validation.split('.').reduce((acc, curr) => acc && acc[curr], this.variables);
                dataInputs[key].status = dataInputs[key].value ? valid ? 'valid' : 'invalid' : 'unused';
            });
        },
        async testTemplate() {
            this.loadingTest = true;
            const accountStore = useAccountStore();
            var prompt = this.template.prompt;
            prompt.max_tokens = parseInt(prompt.max_tokens, 10) || 4000;

            const safePrompt = this.safePrompt;

            const payload = {
                account_id: accountStore.account.account_id,
                prompt: JSON.stringify(safePrompt),
                model: this.template.model,
                debug: 1,
                nocache: 1
            };

            ['id', 'domain', 'manual_url', 'manual_q'].forEach(key => {
                if (this.dataInputs[key].value) payload[key] = this.dataInputs[key].value;
            });

            try {
                const response = await executeLlmTemplate(payload);

                if(response?.data?.broken_messages?.[0].includes('too much empty')) {
                    this.responseError = 'broken_messages';
                    this.response = {};
                    return false;
                } else if(!response?.data?.summary) {
                    this.responseError = 'default';
                    this.response = {};
                    return false;
                } else {
                    this.responseError = null;
                    this.response = {
                        output: response.data.summary,
                        rawInput: response.data.oai_input_json,
                    };
                }

                return true;
            } catch {
                this.responseError = 'default';
                this.response = {};
                return false;
            } finally {
                this.loadingTest = false;
            }
        },
        async saveTemplate() {
            const accountStore = useAccountStore();

            const payload = {
                account_id: accountStore.account.account_id,
                ...this.template,
                ...{prompt: JSON.stringify(this.template.prompt)},
                ...{template: this.template.id},
            };

            delete payload.id; // ID is used locally for the template, but not for the API

            Object.keys(this.requiredDataInputs).forEach(key => {
                if (this.requiredDataInputs[key] === true && this.dataInputs[key].value) {
                    payload[key] = this.dataInputs[key].value;
                }
            });


            try {
                this.loadingSave = true;

                const response = !this.existingTemplate ? await createLlmTemplate(payload) : await updateLlmTemplate(payload);
                if(response?.data?.llm_template[0]?.template) {
                    if(!this.existingTemplate) {
                        router.push({
                            name: 'llm-template',
                            params: {
                                id: this.template.id
                            }
                        });
                    }
                    this.hasSaved = true;
                    return true;
                } else {
                    return false;
                }
            } catch (error) {
                return error;
            } finally {
                this.loadingSave = false; 
            }
        }
    }
});